import API_CONFIG from '../../config/api.config';
import { showToast } from '../../config/toast.config';
import AuthService from './auth.service';
import { vehicleApi } from '../../hooks/useVehicles';

/**
 * @typedef {Object} TransferVehicleRequest
 * @property {string} sku - Vehicle identifier
 * @property {number} quantity - Number of vehicles to transfer
 * @property {number} source_warehouse_area_id - ID of the source area
 * @property {number} destination_warehouse_area_id - ID of the destination area
 */

/**
 * @typedef {Object} TransferVehicleResponse
 * @property {string} status - Response status
 * @property {string} message - Response message
 * @property {number} product_id - DB ID of the vehicle product
 * @property {Object} source_area - Source area information
 * @property {number} source_area.area_id - ID of the source warehouse area
 * @property {string} source_area.area_name - Name of the source warehouse area
 * @property {number} source_area.source_location_id - DB ID of the specific location record debited
 * @property {number} source_area.remaining_quantity - Quantity remaining in the source location
 * @property {Object} destination_area - Destination area information
 * @property {number} destination_area.area_id - ID of the destination warehouse area
 * @property {string} destination_area.area_name - Name of the destination warehouse area
 * @property {number} destination_area.new_location_id - DB ID of the newly created location record
 * @property {number} destination_area.new_quantity - Quantity in the new destination location
 * @property {number} transferred_quantity - The quantity that was transferred
 */

class VehicleService {
  static instance = null;

  static getInstance() {
    if (!VehicleService.instance) {
      VehicleService.instance = new VehicleService();
    }
    return VehicleService.instance;
  }

  constructor() {
    // No initialization needed - React Query handles caching
  }

  async getVehicles() {
    try {
      // Use the vehicleApi directly to fetch data
      const data = await vehicleApi.getVehicles();
      return data;
    } catch (error) {
      showToast.error(error);
      console.error('Error fetching vehicles data:', error);
      throw error;
    }
  }

  async getModelAssociations() {
    try {
      // Use the vehicleApi directly
      const data = await vehicleApi.getModelAssociations();
      return data;
    } catch (error) {
      showToast.error(error);
      console.error('Error fetching model associations:', error);
      throw error;
    }
  }

  async getModelEngines(modelId) {
    try {
      return await vehicleApi.getModelEngines(modelId);
    } catch (error) {
      showToast.error(error);
      console.error('Error fetching model engines:', error);
      throw error;
    }
  }

  async getBodyTypes() {
    try {
      return await vehicleApi.getBodyTypes();
    } catch (error) {
      showToast.error(error);
      console.error('Error fetching vehicle body types:', error);
      throw error;
    }
  }

  async getBoltPatterns() {
    try {
      return await vehicleApi.getBoltPatterns();
    } catch (error) {
      showToast.error(error);
      console.error('Error fetching bolt patterns:', error);
      throw error;
    }
  }

  /**
   * Transfers a vehicle between warehouse areas
   * @param {TransferVehicleRequest} transferData - The transfer data
   * @returns {Promise<TransferVehicleResponse>} The transfer response
   */
  async transferVehicle(transferData) {
    try {
      const url = `${API_CONFIG.BASE_URL}/stoc/async/transfer_vehicle`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getAccessToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(transferData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to transfer vehicle');
      }

      if (data.status !== 'success') {
        throw new Error(data.message || 'Vehicle transfer API returned non-success status');
      }

      return data;
    } catch (error) {
      console.error('Error transferring vehicle:', error);
      throw error;
    }
  }
}

export default VehicleService.getInstance(); 